import { PaymentCalculationResult } from "../models/paymentDetails/paymentCalculationResult";
import { PaymentCalculationSummary } from "../models/paymentDetails/paymentCalculationSummary";
import { PaymentFeeCalculationResult } from "../models/paymentFees/paymentFeeCalculationResult";

/*
 * The surcharge line and subtotal shown to the payer, resolved from the calculate-payment summary.
 */
export interface ResolvedPaymentSummaryState {
  surcharge: PaymentFeeCalculationResult | null;
  combinedSubtotal: number;
  // False when the calc failed, so Pay Now can be blocked instead of hiding a surcharge.
  resolved: boolean;
  // True when the surcharge is quoted once the card is known.
  surchargeQuoteRequired: boolean;
  // The quoted surcharge's label code, known before the card; undefined without a quote.
  surchargeFeeLabel?: string;
}

/*
 * Resolves the surcharge and subtotal to display from a calculate-payment summary that may be
 * undefined. calculatePaymentResults swallows transient errors (a 500 or network blip) and returns
 * undefined; treating that as a zero subtotal makes the Pay Now button read "Pay 0.00". Fall back to
 * the cart total the payer is charging instead, and drop any stale surcharge from a previous method.
 */
export const resolvePaymentSummaryState = (
  summary: PaymentCalculationSummary | undefined,
  cartTotalToCharge: number
): ResolvedPaymentSummaryState => {
  if (!summary) {
    return { surcharge: null, combinedSubtotal: cartTotalToCharge, resolved: false, surchargeQuoteRequired: false };
  }

  return {
    surcharge: summary.surcharge ?? null,
    // A genuine 0 subtotal (empty cart) is kept; only a missing value falls back to the cart total.
    combinedSubtotal: summary.combinedSubtotal ?? cartTotalToCharge,
    resolved: true,
    surchargeQuoteRequired: summary.surchargeQuoteRequired === true,
    surchargeFeeLabel: summary.surchargeQuoteRequired === true ? summary.surchargeFeeLabel ?? undefined : undefined,
  };
};

/*
 * Finds a PaymentCalculationResult or throws an exception
 */
export const findValidPaymentCalculationResultFromTransactionId = (
  transactionId: string,
  paymentCalculationResults?: PaymentCalculationResult[]
): PaymentCalculationResult => {
  const paymentCalculation = paymentCalculationResults?.find((x) => x.id === transactionId);
  if (!paymentCalculation) throw Error("Payment calculation for transaciton was not found");
  return paymentCalculation;
};

/*
 * Calculates the maximum amount that can be paid on the transaction
 */
export const calculateMaximumAmountToPay = (paymentCalculationResult: PaymentCalculationResult): number => {
  // Can't edit the amount so return the total amount to charge
  if (!paymentCalculationResult.amountIsEditable) {
    return paymentCalculationResult.totalAmountToCharge;
  }

  // Use the configured maximum only when it's a positive value at or below the full charge; a
  // missing, zero, or negative maximum means "no cap" → fall back to the full charge. This mirrors
  // the backend EffectiveMaximumToCharge so a negative (invalid) maximum can't cap below zero.
  const { maximumAmountToPay, totalAmountToCharge } = paymentCalculationResult;
  return maximumAmountToPay != null && maximumAmountToPay > 0 && maximumAmountToPay <= totalAmountToCharge
    ? maximumAmountToPay
    : totalAmountToCharge;
};

/*
 * Calculates the minimum amount that can be paid on the transaction
 */
export const calculateMinimumAmountToPay = (paymentCalculationResult: PaymentCalculationResult): number => {
  // Can't edit the amount so return the total amount to charge
  if (!paymentCalculationResult.amountIsEditable) {
    return paymentCalculationResult.totalAmountToCharge;
  }

  // Check that the minimum amount is not less than zero
  let minimum = paymentCalculationResult.minimumAmountToPay || undefined;
  if (!minimum || minimum < 0) {
    minimum = 0;
  }

  // Check that the minimum amount is not greater than the total amount that can be paid
  if (minimum > paymentCalculationResult.totalAmountToCharge) {
    minimum = paymentCalculationResult.totalAmountToCharge;
  }
  return minimum;
};
